(function () {
  function getVariantFromOptions(variants, optionSelects) {
    if (!Array.isArray(variants) || !optionSelects?.length) {
      return null;
    }

    // Sort option selects by their index so options map correctly
    const orderedSelects = optionSelects.slice().sort((selectA, selectB) => {
      const indexA = String(selectA.getAttribute("data-index") || "").replace("option", "");
      const indexB = String(selectB.getAttribute("data-index") || "").replace("option", "");
      return (parseInt(indexA || "0", 10) || 0) - (parseInt(indexB || "0", 10) || 0);
    });
    const selectedValues = orderedSelects.map((select) => select.value);

    // Find the variant whose options array matches all selected values
    for (const variant of variants) {
      if (!variant) {
        continue;
      }
      const isMatch = selectedValues.every((selectedValue, optionIndex) => {
        // Skip unset option groups (products with fewer than 3 options)
        if (!selectedValue) {
          return true;
        }
        return variant.options[optionIndex] === selectedValue;
      });
      if (isMatch) {
        return variant;
      }
    }
    return null;
  }

  function normalizeVariants(variants) {
    return variants.map((variant) => {
      if (!variant) {
        return variant;
      }

      // Old Shopify JSON uses "option1/2/3" not "options" array
      // https://shopify.dev/docs/api/liquid/objects/variant#variant-option1
      if (Array.isArray(variant.options) && variant.options.length > 0) {
        return variant;
      }
      const options = [variant.option1, variant.option2, variant.option3].filter(
        (value) => value !== null && value !== undefined,
      );
      return { ...variant, options };
    });
  }

  function readVariantsJson(container) {
    if (!container) {
      return null;
    }

    // Read the variants from the JSON data
    const jsonScripts = container.querySelectorAll('script[type="application/json"]');

    // Find the variant JSON data
    for (const script of jsonScripts) {
      const isVariantJson = script.hasAttribute("data-variant-json");
      const isProductJson = [...script.attributes].some((attribute) =>
        attribute.name.startsWith("data-product-json"),
      );

      // Skip scripts that do not contain variant data
      if (!isVariantJson && !isProductJson) {
        continue;
      }

      try {
        const parsed = JSON.parse(script.textContent);
        if (Array.isArray(parsed)) {
          return normalizeVariants(parsed);
        }
      } catch (error) {
        // Try the next script if this one is not valid JSON
        continue;
      }
    }
    return null;
  }

  // Toggle the visibility of the variant elements
  function toggleVariantElements(root, variantId, selector) {
    root.querySelectorAll(selector).forEach((element) => {
      const elementVariantId = Number(element.getAttribute("data-product-id"));
      const isHidden = elementVariantId !== Number(variantId);
      element.classList.toggle("hidden", isHidden);
    });
  }

  // Initialize the variant picker
  function initQuickViewVariantPicker(quickViewRoot, options = {}) {
    // Avoid binding listeners more than once on the same quick view instance
    if (!quickViewRoot || quickViewRoot.__quickViewVariantPickerBound) {
      return;
    }

    // Read the variants from the JSON data
    const variants = readVariantsJson(quickViewRoot);
    if (!Array.isArray(variants) || variants.length === 0) {
      return;
    }

    // Get the option selects
    const optionSelects = Array.from(
      quickViewRoot.querySelectorAll('select[name^="options["]'),
    );
    if (!optionSelects.length) {
      return;
    }

    // Mark the quick view as bound to the variant picker
    quickViewRoot.__quickViewVariantPickerBound = true;

    // Get the variant select and details links
    const variantSelect = quickViewRoot.querySelector("[data-variant-select]");
    const detailsLinks = Array.from(
      quickViewRoot.querySelectorAll(
        ".quick-view-view-product-details-link[data-product-id]",
      ),
    );

    // Apply the variant to the quick view
    function applyVariant(variant) {
      if (!variant) {
        return;
      }

      // Keep the hidden variant select in sync with the visible picker
      if (variantSelect) {
        const matchingOption = variantSelect.querySelector(`option[value="${variant.id}"]`);
        if (matchingOption) {
          matchingOption.selected = true;
          variantSelect.value = String(variant.id);
        }
      }

      // Show the packshot for the selected variant and hide the others
      toggleVariantElements(
        quickViewRoot,
        variant.id,
        ".quickview-image[data-product-id]",
      );

      // Show the product-details link for the selected variant only
      detailsLinks.forEach((link) => {
        const linkVariantId = Number(link.getAttribute("data-product-id"));
        link.classList.toggle("hidden", linkVariantId !== Number(variant.id));
      });

      // Run caller-specific updates (e.g. BYOB price and add-to-bundle button)
      if (typeof options.onVariantApply === "function") {
        options.onVariantApply(variant, quickViewRoot);
      }

      // Keep quick-view updates isolated from the PDP variant/gallery handlers.
      document.dispatchEvent(
        new CustomEvent("quick-view:variant-change", {
          detail: { variant, root: quickViewRoot },
        }),
      );
    }

    function recomputeVariant() {
      const selectedVariant = getVariantFromOptions(variants, optionSelects);
      applyVariant(selectedVariant);
    }

    // Listen for native select changes
    optionSelects.forEach((select) => {
      if (select.__quickViewVariantPickerSelectBound) {
        return;
      }
      select.__quickViewVariantPickerSelectBound = true;
      select.addEventListener("change", recomputeVariant);
    });

    // Listen for button clicks and sync them back to the hidden select
    quickViewRoot.querySelectorAll("[data-option-buttons]").forEach((buttonGroup) => {
      const select = buttonGroup.querySelector("select");
      const buttons = buttonGroup.querySelectorAll("[data-button]");

      if (!select || !buttons.length || buttonGroup.__quickViewVariantPickerGroupBound) {
        return;
      }

      buttonGroup.__quickViewVariantPickerGroupBound = true;

      buttons.forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();

          const optionHandle = button.getAttribute("data-option-handle");
          if (!optionHandle) {
            return;
          }

          const matchingOption = select.querySelector(`[data-value-handle="${optionHandle}"]`);
          if (!matchingOption) {
            return;
          }

          matchingOption.selected = true;
          select.value = matchingOption.value;

          // Update selected state on the visible buttons
          buttons.forEach((groupButton) =>
            groupButton.classList.toggle(
              "selected",
              groupButton.getAttribute("data-option-handle") === optionHandle,
            ),
          );

          // Trigger the same change flow as a native select change
          select.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });
    });

    // Sync UI to the initially selected variant on load
    recomputeVariant();
  }

  window.initQuickViewVariantPicker = initQuickViewVariantPicker;
})();
